import { UserTypes } from '@shared/auth/index.ts';
import { ConsultWaitTime, ConsultWaitTimeData } from '@shared/consult/index.ts';
import { type HealthCategory } from '@shared/forms/index.ts';

/**
 * TYPES
 */

export type WaitTimeRole = (typeof waitTimeRoles)[number];

export type SelectedConfigKey = keyof typeof SELECTED_CONSULT_CONFIG;

export type SelectedConsultField = (typeof selectedConsultFieldList)[number];
export type UnmodifiedConsultField = (typeof unModifiedConsultFieldList)[number];

export type SelectedConsults = Record<SelectedConsultField, ConsultWaitTime>;
export type UnmodifiedConsults = Record<UnmodifiedConsultField, ConsultWaitTime>;

export type TransformSelectedConsultStatesReturn<T extends boolean | undefined> = T extends true
  ? UnmodifiedConsults
  : SelectedConsults;

export interface TransformSelectedConsultStatesParams<T extends boolean | undefined = false> {
  consultTimeData: ConsultWaitTimeData;
  isUnModified?: T;
  selectedCategoryKey?: WhiteSpaceToScreamingSnakeCase<HealthCategory>;
}

/**
 * CONSTANTS
 */

const { NURSE, DIRECTOR } = UserTypes;
export const isAllowedToUpdate: (typeof UserTypes)[keyof typeof UserTypes][] = [NURSE, DIRECTOR];

export const waitTimeRoles = ['DOCTOR', 'NURSE'] as const;

export const defaultTimePickerState: ConsultWaitTime = {
  timeInMins: 0,
  duration: 0,
  durationUnit: 'DAY',
};

export const SELECTED_CONSULT_CONFIG = {
  DOCTOR_INITIAL: {
    key: 'selectedDoctorInitialConsult',
    role: 'DOCTOR',
    type: 'INITIAL',
    dbKey: 'initialConsultWaitTime',
    unModifiedKey: 'unModifiedDoctorInitialConsult',
    id: 'DOCTOR_INITIAL',
  },
  DOCTOR_CHECKUP: {
    key: 'selectedDoctorCheckupConsult',
    role: 'DOCTOR',
    type: 'CHECKUP',
    dbKey: 'checkupConsultWaitTime',
    unModifiedKey: 'unModifiedDoctorCheckupConsult',
    id: 'DOCTOR_CHECKUP',
  },
  NURSE_INITIAL: {
    key: 'selectedNurseInitialConsult',
    role: 'NURSE',
    type: 'INITIAL',
    dbKey: 'initialConsultWaitTime',
    unModifiedKey: 'unModifiedNurseInitialConsult',
    id: 'NURSE_INITIAL',
  },
  NURSE_CHECKUP: {
    key: 'selectedNurseCheckupConsult',
    role: 'NURSE',
    type: 'CHECKUP',
    dbKey: 'checkupConsultWaitTime',
    unModifiedKey: 'unModifiedNurseCheckupConsult',
    id: 'NURSE_CHECKUP',
  },
} as const;

export const selectedConsultFieldList = Object.values(SELECTED_CONSULT_CONFIG).map(({ key }) => key);
export const unModifiedConsultFieldList = Object.values(SELECTED_CONSULT_CONFIG).map(
  ({ unModifiedKey }) => unModifiedKey,
);

export const defaultConsultWaitTimeState = {
  isLoading: false,
  consultWaitTime: {} as ConsultWaitTimeData,
  unModifiedConsultWaitTime: {} as ConsultWaitTimeData,
  selectedCategory: '' as WhiteSpaceToScreamingSnakeCase<HealthCategory> | '',
  ...(Object.fromEntries(selectedConsultFieldList.map((key) => [key, defaultTimePickerState])) as SelectedConsults),
};

/**
 * FUNCTIONS
 */

/**
 * Transforms the consult time data to get the `selectedConsultFieldList` states.
 */
export const transformSelectedConsultStates = <T extends boolean | undefined = false>({
  consultTimeData,
  isUnModified,
  selectedCategoryKey,
}: TransformSelectedConsultStatesParams<T>): TransformSelectedConsultStatesReturn<T> => {
  return Object.values(SELECTED_CONSULT_CONFIG).reduce<TransformSelectedConsultStatesReturn<T>>(
    (acc, { key, role, dbKey, unModifiedKey }) => {
      const consultWaitTime = selectedCategoryKey
        ? consultTimeData[selectedCategoryKey]?.[role]?.[dbKey]
        : defaultTimePickerState;

      return {
        ...acc,
        [isUnModified ? unModifiedKey : key]: consultWaitTime,
      } as TransformSelectedConsultStatesReturn<T>;
    },
    {} as TransformSelectedConsultStatesReturn<T>,
  );
};
